// Workspace-project private-data loader.
//
// Drives the workspace-project app's data hydration in response to auth
// transitions. Extracted from the app.js root onCreate closure because
// the smbls 3.14 auto-router mounts page DOMQL components directly into
// `Slot` from the shell's pages registry — the workspace-project wrapper
// never instantiates, so its `onCreate` never fires. The shell's
// `subscribeAuthSession` runs these callbacks instead. Kept as plain
// functions (not lifecycle hooks) so either entry point — shell auth
// subscription or sub-app onCreate when run standalone — can invoke them.
//
// `loadPrivateData` runs once per sign-in transition and hydrates
// people/teams/roles/userDocs/userPrefs + financials. `onWorkspaceChange`
// runs when the JWT's `active_workspace_id` flips for the same user
// (org switch); it re-fetches workspace-scoped tables in place + rebinds
// realtime channels so the UI never has to full-page-reload.

import { initials } from './index.js'
import { loadAllTeams } from './teams.js'
import { fetchOrgNotifCount } from './notifications/fetchOrgNotifCount.js'
import { grants } from '@symbo.ls/finance-features'
import { getRealtimeManager } from './workspaceRealtime.js'
import { clearWorkspaceSwitchFallback } from '../components/AppWorkspaceSwitcher.js'
import { USE_FINANCIALS_PROJECT } from './featureFlags.js'
import { readGovernanceClaims } from '../../shared/functions/diagnoseAuth.js'

// Fresh-login race fix: SDK fires onAuthStateChange when symbols_access_token
// lands, BEFORE _fanOutBridgeLogin mints the governance JWT. Without the gov
// JWT, every /workspace/* call 401s ("missing bearer token"). Poll briefly
// for the gov JWT to land before starting the data hydration.
const _waitForGovernanceJWT = async (timeoutMs = 10000, intervalMs = 100) => {
  const claims = readGovernanceClaims()
  if (claims?.sub) return true
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, intervalMs))
    const c = readGovernanceClaims()
    if (c?.sub) return true
  }
  return false
}

// Symbols org — Mongo ObjectId. Currently a constant; will come from the
// JWT's active_workspace_id → workspace_org_id once the dual-source
// migration window closes.
const SYMBOLS_ORG_ID = '69b77a61e0e0de1aa47d01cb'

const _person = (r) => ({
  userId: r.user_id,
  email: r.email,
  name: r.name || 'Unknown',
  avatar: r.avatar || initials(r.name),
  position: r.position,
  location: r.location,
  timezone: r.timezone,
  github: r.github,
  linkedin: r.linkedin,
  twitter: r.twitter,
  birthday: r.birthday,
  start_date: r.start_date,
  end_date: r.end_date,
  status: r.status,
  monthly_stocks: r.monthly_stocks,
  stocks: r.stocks,
  bio: r.bio,
  pronouns: r.pronouns,
  startDate: r.start_date,
  endDate: r.end_date,
  monthlyStocks: r.monthly_stocks
})

const filterToActiveOrg = (peopleAll, root) => {
  const orgMembersByOrgId = root?.orgMembersByOrgId || {}
  const activeOrgId = root?.org?.id || root?.jwtClaims?.workspace_org_id
  const memberSet = activeOrgId ? orgMembersByOrgId[activeOrgId] : null
  if (memberSet && typeof memberSet === 'object') {
    const set = memberSet instanceof Set ? memberSet : new Set(Object.keys(memberSet))
    if (set.size > 0) {
      const lower = new Set(Array.from(set).map((e) => String(e || '').toLowerCase()))
      return peopleAll.filter((p) => lower.has(String(p?.email || '').toLowerCase()))
    }
  }
  // Fallback: only return self when no member set is loaded
  const myEmail = String(root?.currentUserEmail || '').toLowerCase()
  if (!myEmail) return []
  return peopleAll.filter((p) => String(p?.email || '').toLowerCase() === myEmail)
}

const _loadRoles = async (root, sdk) => {
  try {
    const orgId = root?.jwtClaims?.workspace_org_id
    if (!orgId) return
    const result = await sdk.getOrgMemberRoles(orgId).catch(() => null)
    const { members = [] } = result || {}
    const emailToUserId = {}
    for (const p of (root.peopleAll || [])) {
      if (p.email && p.userId) emailToUserId[p.email.toLowerCase()] = p.userId
    }
    const byUser = {}
    for (const m of members) {
      const uid = emailToUserId[(m.email || '').toLowerCase()]
      if (uid) byUser[uid] = m.role
    }
    const prev = root.roles || { byUser: {}, version: 0 }
    root.update({
      roles: {
        ...prev,
        byUser,
        version: (prev.version || 0) + 1
      }
    })
  } catch (e) {
    console.warn('[workspace-project] member-roles fetch failed', e)
  }
}

export const loadPrivateData = async (root, sdk) => {
  if (!root || !sdk) return

  // Wait for the governance JWT before any /workspace/* calls — SDK
  // login fires onAuthStateChange the moment symbols_access_token
  // lands, but federation (sdk-bridge loginAll) mints the governance
  // JWT a few hundred ms later. Without it, every workspace call 401s
  // because WorkspaceProjectService._resolveAuthHeader prefers the
  // governance JWT and the fallback path is intentionally rejected by
  // the wrapper for workspace-scoped routes.
  const govReady = await _waitForGovernanceJWT()
  if (!govReady) {
    console.warn('[workspace-project] governance JWT did not land within 10s — continuing with calls that may 401')
  }

  // People + announcements + stories + resourceLinks come from the
  // workspace-project app's declarative `fetch: [...]` array, which only
  // runs when the wrapper component mounts. With the smbls auto-router
  // mounting pages directly into Slot the wrapper never instantiates, so
  // we hydrate those tables here. Run people first so `loadAllTeams`
  // (org-member filter) and `_loadRoles` (email→userId map) see the data.
  try {
    const data = await sdk.execute('workspaceProject.people', 'list')
    if (data) {
      const all = (data || []).map(_person)
      root.update({ peopleAll: all, people: filterToActiveOrg(all, root) })
    }
  } catch (e) {
    console.warn('[workspace-project] people fetch failed', e)
  }

  const me = (root.peopleAll || []).find(p => p.email === root.currentUserEmail)
  if (me && me.status === 'left') {
    await sdk.logout()
    if (typeof window !== 'undefined') window.location.reload()
    return
  }
  await Promise.all([
    _loadRoles(root, sdk),
    sdk.execute('workspaceProject.announcements', 'list', { order: 'date' })
      .then((data) => {
        if (!data) return
        root.update({
          announcements: data.map((r) => ({
            id: r.id, title: r.title, author: r.author,
            authorAvatar: r.author_avatar || initials(r.author),
            authorRole: r.author_role, date: r.date,
            readTime: r.read_time, tag: r.tag, cover: r.cover,
            excerpt: r.excerpt, body: r.body, reactions: r.reactions || {}
          }))
        })
      })
      .catch(e => console.warn('[workspace-project] announcements fetch failed', e)),
    sdk.execute('workspaceProject.stories', 'list', { order: 'id' })
      .then((data) => { if (data) root.update({ stories: data }) })
      .catch(e => console.warn('[workspace-project] stories fetch failed', e)),
    sdk.execute('workspaceProject.documents.resourceLinks', 'list')
      .then((data) => { if (data) root.update({ resourceLinks: data }) })
      .catch(e => console.warn('[workspace-project] resource_links fetch failed', e)),
    loadAllTeams(root)
      .then((result) => {
        const { teams = [], teamsByEmail = {}, orgMembersByOrgId = {} } = result || {}
        const people = filterToActiveOrg(root.peopleAll || [], {
          ...root,
          orgMembersByOrgId
        })
        root.update({ teams, teamsByEmail, orgMembersByOrgId, people })
      })
      .catch(e => console.warn('[workspace-project] teams load failed', e)),
    sdk.execute('workspaceProject.documents.userDocuments', 'list', { userId: root.currentUserId })
      .then((data) => { if (data) root.update({ userDocs: data }) })
      .catch(e => console.warn('[workspace-project] user_documents fetch failed', e)),
    sdk.execute('workspaceProject.userPreferences', 'get')
      .then((data) => { if (data) root.update({ userPrefs: data }) })
      .catch(e => console.warn('[workspace-project] user_preferences fetch failed', e)),
    USE_FINANCIALS_PROJECT
      ? grants.getCompanySettings(SYMBOLS_ORG_ID)
          .then(({ data }) => { if (data) root.update({ companySettings: data }) })
          .catch(e => console.warn('[workspace-project] financials.company_settings fetch failed', e))
      : sdk.execute('workspaceProject.companySettings', 'get')
          .then((data) => { if (data) root.update({ companySettings: data }) })
          .catch(e => console.warn('[workspace-project] company_settings fetch failed', e)),
    USE_FINANCIALS_PROJECT
      ? grants.listGrants({ orgId: SYMBOLS_ORG_ID })
          .then(({ data }) => { if (data) root.update({ userGrants: data }) })
          .catch(e => console.warn('[workspace-project] financials.grants fetch failed', e))
      : sdk.execute('workspaceProject.userGrants', 'list', { order: 'vesting_start' })
          .then((data) => { if (data) root.update({ userGrants: data }) })
          .catch(e => console.warn('[workspace-project] user_grants fetch failed', e)),
    USE_FINANCIALS_PROJECT
      ? grants.listValuations(SYMBOLS_ORG_ID)
          .then(({ data }) => { if (data) root.update({ valuations: data }) })
          .catch(e => console.warn('[workspace-project] financials.valuations fetch failed', e))
      : sdk.execute('workspaceProject.valuations', 'list', { order: 'amount' })
          .then((data) => { if (data) root.update({ valuations: data }) })
          .catch(e => console.warn('[workspace-project] valuations fetch failed', e)),
    fetchOrgNotifCount(root)
      .catch(e => console.warn('[workspace-project] org-notifications fetch failed', e))
  ])
}

export const onWorkspaceChange = async (root, sdk, _newClaims) => {
  if (!root || !sdk) return
  clearWorkspaceSwitchFallback()

  try {
    const { teams = [], teamsByEmail = {}, orgMembersByOrgId = {} } =
      await loadAllTeams(root)
    const people = filterToActiveOrg(root.peopleAll || [], {
      ...root,
      orgMembersByOrgId
    })
    root.update({ teams, teamsByEmail, orgMembersByOrgId, people })
  } catch (e) {
    console.warn('[workspace-project] workspace-change: teams reload failed', e)
  }

  await Promise.all([
    sdk.execute('workspaceProject.people', 'list')
      .then((data) => {
        if (!data) return
        const all = data.map(_person)
        const people = filterToActiveOrg(all, root)
        root.update({ peopleAll: all, people })
      })
      .catch(e => console.warn('[workspace-project] workspace-change: people fetch failed', e)),
    sdk.execute('workspaceProject.announcements', 'list', { order: 'date' })
      .then((data) => {
        if (!data) return
        root.update({
          announcements: data.map((r) => ({
            id: r.id, title: r.title, author: r.author,
            authorAvatar: r.author_avatar || initials(r.author),
            authorRole: r.author_role, date: r.date,
            readTime: r.read_time, tag: r.tag, cover: r.cover,
            excerpt: r.excerpt, body: r.body, reactions: r.reactions || {}
          }))
        })
      })
      .catch(e => console.warn('[workspace-project] workspace-change: announcements fetch failed', e)),
    (() => {
      const min = new Date(Date.now() - 1 * 86400000).toISOString()
      const max = new Date(Date.now() + 14 * 86400000).toISOString()
      return sdk.execute('workspaceProject.calendar', 'list', {
        filter: { deleted: false, start_at: { gte: min, lte: max } },
        order: 'start_at',
        limit: 500
      })
        .then((data) => {
          if (!data) return
          const me = root.currentUserEmail || ''
          const byKey = new Map()
          for (const r of data) {
            const key = r.google_event_id || `local-${r.id}`
            const existing = byKey.get(key)
            if (!existing) { byKey.set(key, r); continue }
            if (r.google_calendar_id === me && existing.google_calendar_id !== me) {
              byKey.set(key, r)
            }
          }
          root.update({
            intranetEvents: [...byKey.values()].map((r) => ({
              id: r.id, title: r.title, startAt: r.start_at, endAt: r.end_at,
              location: r.description ? r.description.slice(0, 60) : '',
              attendees: Array.isArray(r.invitees) ? r.invitees : [],
              organizerEmail: r.organizer_email
            }))
          })
        })
        .catch(e => console.warn('[workspace-project] workspace-change: calendar_events fetch failed', e))
    })(),
    sdk.execute('workspaceProject.stories', 'list', { order: 'id' })
      .then((data) => { if (data) root.update({ stories: data }) })
      .catch(e => console.warn('[workspace-project] workspace-change: stories fetch failed', e)),
    sdk.execute('workspaceProject.documents.resourceLinks', 'list')
      .then((data) => { if (data) root.update({ resourceLinks: data }) })
      .catch(e => console.warn('[workspace-project] workspace-change: resource_links fetch failed', e)),
    sdk.execute('workspaceProject.documents.userDocuments', 'list', { userId: root.currentUserId })
      .then((data) => { if (data) root.update({ userDocs: data }) })
      .catch(e => console.warn('[workspace-project] workspace-change: user_documents fetch failed', e)),
    fetchOrgNotifCount(root)
      .catch(e => console.warn('[workspace-project] workspace-change: org-notifications fetch failed', e))
  ])

  try {
    const mgr = getRealtimeManager(root)
    mgr.rebindAll()
  } catch (e) {
    console.warn('[workspace-project] workspace-change: realtime rebind failed', e)
  }

  // Clear both the switch overlay AND the optimistic-pick override now
  // that the JWT refresh + data reload completed. `_pendingActiveOrgId`
  // is what `_activeOrg` reads to keep the dropdown looking switched
  // during the round-trip; once the real `jwtClaims.active_workspace_id`
  // catches up we let the JWT-derived path own the truth again.
  root.update({ _wsSwitching: null, _pendingActiveOrgId: null })
}
